import React, { FC } from 'react';
import { Alert, Spin } from 'antd';
import { styled } from 'styled-components';
import { usePreTimeSuggestions } from '@/api/Inspections';
import { useTranslate } from '@/translations';
import { Icon } from '@/ui/components';
import { PreTimeBand } from '@/types';
import { preTimeBandStyle } from './PreTimeRecommendationTag';
import { PreTimeSparkleIcon } from './PreTimeSparkleIcon';
import {
  PRE_TIME_LOOKBACK_YEARS,
  bandLabelKey,
  countBands,
} from './preTimeSuggestionView';

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const AssistantCard = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1.4 1 320px;
  padding: 16px 18px;
  border: 1px solid #d9e2ff;
  border-radius: 12px;
  background: #f4f6ff;
`;

const AssistantTitle = styled.h2`
  color: #3b5bdb;
  font-size: 16px;
  font-weight: 700;
  margin: 0;
`;

const AssistantBody = styled.p`
  color: #475467;
  font-size: 13px;
  line-height: 1.45;
`;

const StatCard = styled.section<{ $background: string; $border: string }>`
  display: flex;
  flex-direction: column;
  column-gap: 8px;
  flex: 1 1 180px;
  padding: 14px 16px;
  border: 1px solid ${({ $border }) => $border};
  border-radius: 12px;
  background: ${({ $background }) => $background};
`;

const StatCount = styled.span`
  font-size: 22px;
  font-weight: 700;
  line-height: 1.1;
  color: #101828;
`;

const StatHint = styled.span`
  color: #667085;
  font-size: 12px;
`;

const stats: Array<{
  band: PreTimeBand;
  hintKey:
    | 'preTimeReadyToReview'
    | 'preTimeCheckSuggestions'
    | 'preTimeNotEnoughHistory';
  background: string;
  border: string;
}> = [
  {
    band: 'HighConfidence',
    hintKey: 'preTimeReadyToReview',
    background: '#ecfdf3',
    border: '#abefc6',
  },
  {
    band: 'NeedReview',
    hintKey: 'preTimeCheckSuggestions',
    background: '#fffaeb',
    border: '#fedf89',
  },
  {
    band: 'Manual',
    hintKey: 'preTimeNotEnoughHistory',
    background: '#f8f9fb',
    border: '#e4e7ec',
  },
];

export const PreTimeAssistantSummary: FC<{
  inspectionId: number;
  excludedDamageLineIds?: ReadonlySet<number>;
}> = ({ inspectionId, excludedDamageLineIds }) => {
  const { t } = useTranslate();
  const { data, isFetching, isError } = usePreTimeSuggestions(inspectionId);
  const lines = (data ?? []).filter(
    (line) => !excludedDamageLineIds?.has(line.damageLineId),
  );
  const counts = countBands(lines);

  if (isError) {
    return (
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('preTimeNoReviewLines')}
      />
    );
  }
  if (!isFetching && lines.length === 0) return null;

  return (
    <Spin spinning={isFetching}>
      <Row data-testid="pre-time-assistant">
        <AssistantCard>
          <div className="d-flex align-items-center gap-2">
            <PreTimeSparkleIcon color="#3b5bdb" />
            <AssistantTitle>{t('preTimeAssistant')}</AssistantTitle>
          </div>
          <AssistantBody>
            {t('preTimeAssistantBody', {
              years: PRE_TIME_LOOKBACK_YEARS,
              count: lines.length,
            })}
          </AssistantBody>
        </AssistantCard>
        {stats.map((stat) => (
          <StatCard
            key={stat.band}
            $background={stat.background}
            $border={stat.border}
            aria-label={t(bandLabelKey[stat.band])}
          >
            <div className="d-flex align-items-center gap-2">
              <Icon
                icon={preTimeBandStyle[stat.band].icon}
                color={preTimeBandStyle[stat.band].color}
              />
              <StatCount>{counts[stat.band]}</StatCount>
            </div>
            <strong>{t(bandLabelKey[stat.band])}</strong>
            <StatHint>{t(stat.hintKey)}</StatHint>
          </StatCard>
        ))}
      </Row>
    </Spin>
  );
};
